import React, { useEffect } from 'react';
import { Popconfirm, Button, Divider, Spin, message } from 'antd';
import PageLoader from '../../../../components/PageLoader/PageLoader';
import { connect } from 'react-redux';
import OrganisationMainSection from '../../../../components/OrganisationMainSection/OrganisationMainSection';
import Card from '../../../../components/Card/Card';
import Modal from '../../../../components/Modal/Modal';
import Table from '../../../../components/Table/Table';
import Export from '../../../../components/Export/Export';
import Import from '../../../../components/Import/Import';
import ModuleEmptyState from '../../../../components/ModuleEmptyState';
import ContactEdit from './components/ContactEdit';
import * as actions from './ContactAction';
import { parseContactImportResult } from './ContactAction';
import { orgIdSelector, businessNameSelector } from '../../OrganisationSelectors';
import {
  loadingContactsSelector,
  contactListSelector,
  contactTypesSelector,
  workTypesSelector
} from './ContactSelectors';
import { MSG_ERROR } from '../../../../constants';
import { getApiErrorMessage } from '../../../../utils/httpError';

const INFO_MSG = (
  <>
    <p>
      Being able to contact any or all of your organisation&apos;s internal stakeholders, that is employees,
      contractors, probationers, outworkers is extremely important in the event of a crisis. That is why it is one of
      the first pieces of information that is collected on ContinuityCoach.
    </p>
    <p>You have two options to complete this important section.</p>
    <ol>
      <li>
        For an organisation with a relatively small number of internal contacts it may be easier to simply complete the
        fields below by using the &quot;Add new&quot; button. This option should also be used to add internal contacts
        as they join your organisation.
      </li>
      <li>For larger organisations we suggest using the &quot;Import&quot; function.</li>
    </ol>
    <p>
      To Import your Internal Contacts first click the &quot;Import&quot; button so you can download the required
      Template. Ensure you complete the template without adding or deleting any columns, then save the spreadsheet and
      import it straight into ContinuityCoach by dragging and dropping the spreadsheet into the clearly marked
      &quot;drag and drop&quot; field, or click the field to allow a search of your computer&apos;s files.
    </p>
    <p>
      It is important that you complete this task now as you will be able to assign your Internal Contacts to Teams
      later in the Plan process. Completing this will also save you time and stress should &quot;it&quot; ever happen to
      you. Please remember to update the list regularly for new team members and departures.
    </p>
  </>
);

const GUIDANCE = (
  <>
    <strong>What to do:</strong> add people with <strong>Add Contact</strong> or bulk-load them with{' '}
    <strong>Import</strong>. You will assign these contacts to teams later in the plan steps.
  </>
);

const EMPTY_HEADLINE = 'No internal contacts yet';
const EMPTY_BODY =
  'Start building your continuity plan by adding key internal stakeholders who should be contacted during business disruptions.';
const EMPTY_TIP =
  'Tip: Download the CSV template, add your contacts, and upload to import multiple records at once.';

const Contact = ({
  orgId,
  businessName,
  loading,
  contactList,
  fetchContact,
  deleteContact,
  exportContacts,
  importContact,
  downloadContactImportTemplate
}) => {
  useEffect(() => {
    fetchContact(orgId);
  }, [orgId, fetchContact]);

  const onDelete = async contact => {
    try {
      await deleteContact(orgId, contact.contactId);
    } catch (err) {
      message.error(getApiErrorMessage(err, MSG_ERROR));
    }
    fetchContact(orgId);
  };

  const columns = [
    {
      key: 'firstName',
      title: 'First Name',
      dataIndex: 'firstName',
      sorter: (a, b) => a.firstName.localeCompare(b.firstName)
    },
    {
      key: 'lastName',
      title: 'Last Name',
      dataIndex: 'lastName',
      sorter: (a, b) => a.lastName.localeCompare(b.lastName)
    },
    {
      key: 'email',
      title: 'Email',
      dataIndex: 'email',
      width: 200,
      render: text => (text ? <a href={`mailto:${text}`}>{text}</a> : null)
    },
    {
      key: 'phone',
      title: 'Phone',
      dataIndex: 'phone',
      render: text => (text ? <a href={`tel:${text}`}>{text}</a> : null)
    },
    {
      key: 'mobile',
      title: 'Mobile/Cell Phone',
      dataIndex: 'mobile',
      render: text => (text ? <a href={`tel:${text}`}>{text}</a> : null)
    },
    {
      key: 'jobTitle',
      title: 'Job Title',
      dataIndex: 'jobTitle'
    },
    {
      key: 'action',
      width: 115,
      render: (text, record) => (
        <span>
          <Modal explanation toggle={show => <Button type="link" icon="edit" onClick={show} />}>
            {(hide, setExplanation) => (
              <ContactEdit contactInfo={record} close={hide} onExplanationChange={setExplanation} />
            )}
          </Modal>
          <Divider type="vertical" />
          <Popconfirm title="Sure to delete?" onConfirm={() => onDelete(record)}>
            <Button type="link" icon="delete" />
          </Popconfirm>
        </span>
      )
    }
  ];

  const hasContacts = Array.isArray(contactList) && contactList.length > 0;
  const isInitialLoad = loading && !Array.isArray(contactList);

  const toolbarActions = (
    <>
      <Modal
        title="Import Contacts"
        toggle={show => (
          <Button
            type="default"
            className="organisation-main-section__toolbar-ghost"
            icon="contacts"
            htmlType="button"
            onClick={show}
          >
            Import
          </Button>
        )}
      >
        {() => (
          <Import
            templateName="contacts_template.xlsx"
            downloadTemplate={downloadContactImportTemplate}
            importFromTemplate={file => importContact(orgId, file)}
            parseImportResult={parseContactImportResult}
          />
        )}
      </Modal>
      <Export
        fileName={`${businessName.replace(/\s+/g, '_')}_Contacts.xlsx`}
        exportFile={() => exportContacts(orgId)}
      >
        {onExport => (
          <Button
            type="default"
            className="organisation-main-section__toolbar-ghost"
            icon="export"
            htmlType="button"
            onClick={onExport}
          >
            Export
          </Button>
        )}
      </Export>
      <Modal
        explanation
        toggle={show => (
          <Button
            type="primary"
            className="organisation-main-section__toolbar-primary"
            htmlType="button"
            onClick={show}
          >
            Add Contact
          </Button>
        )}
      >
        {(hide, setExplanation) => <ContactEdit close={hide} onExplanationChange={setExplanation} />}
      </Modal>
    </>
  );

  const contactEmptySlot = (
    <ModuleEmptyState
      moduleTitle="Internal Contacts"
      embedded
      headline={EMPTY_HEADLINE}
      description={EMPTY_BODY}
      tip={EMPTY_TIP}
      iconType="user"
    >
      <Modal
        explanation
        toggle={show => (
          <Button type="primary" className="module-empty-state__btn-primary" htmlType="button" onClick={show}>
            Add First Contact
          </Button>
        )}
      >
        {(hide, setExplanation) => <ContactEdit close={hide} onExplanationChange={setExplanation} />}
      </Modal>
      <Modal
        title="Import Contacts"
        toggle={show => (
          <Button className="module-empty-state__btn-secondary" htmlType="button" onClick={show}>
            Import from CSV
          </Button>
        )}
      >
        {() => (
          <Import
            templateName="contacts_template.xlsx"
            downloadTemplate={downloadContactImportTemplate}
            importFromTemplate={file => importContact(orgId, file)}
            parseImportResult={parseContactImportResult}
          />
        )}
      </Modal>
    </ModuleEmptyState>
  );

  return isInitialLoad ? (
    <PageLoader tip="Loading Contacts..." />
  ) : (
    <OrganisationMainSection
      title="Internal Contacts"
      tooltipContent={INFO_MSG}
      guidance={GUIDANCE}
      toolbarActions={toolbarActions}
      flowNext="/organization/customers"
    >
      <Spin spinning={loading}>
        {hasContacts ? (
          <Card>
            <Table
              title="Contacts"
              columns={columns}
              dataSource={contactList}
              rowKey="contactId"
              searchFields={['firstName', 'lastName', 'email', 'phone', 'mobile', 'jobTitle']}
            />
          </Card>
        ) : (
          contactEmptySlot
        )}
      </Spin>
    </OrganisationMainSection>
  );
};

const mapStateToProps = state => ({
  orgId: orgIdSelector(state),
  businessName: businessNameSelector(state),
  loading: loadingContactsSelector(state),
  contactList: contactListSelector(state),
  contactTypes: contactTypesSelector(state),
  workTypes: workTypesSelector(state)
});

const mapDispatchToProps = { ...actions };

export default connect(mapStateToProps, mapDispatchToProps)(Contact);
